import type { IncomingMessage, ServerResponse } from 'http'

import { resolveTenantSubdomainFromHostname } from '@/lib/resolve-tenant-subdomain'
import { resolveDefaultThumbnailUrl } from '@/lib/tenant-media-defaults'

import { resolveRuntimeBrandingMetadata } from '@/configs/schema-metadata'
import { APP_HOST_HEADER } from '@/constant/app'
import { baseService } from '@/services/base.service'
import { communityConfigService } from '@/services/community-config.service'
import communityMediaDefaultService from '@/services/community-media-default.service'
import type { TenantConfig, TenantCustomization } from '@/services/tenant.service'
import { isCommunityPubliclyAccessible } from '@/utils/community-status.utils'
import { hostnameToBrandName, isFsdsHostname } from '@/utils/domain-branding-resolver'
import { getAbsoluteUrl, resolveSeoOgImageUrl } from '@/utils/seo'
import {
  getTenantFromRequest,
  normalizeTenantDisplayName,
  usesFsdsPlatformTenantConfig,
} from '@/utils/tenant.utils'
import {
  getTenantConfigFromCookie,
  setTenantConfigCookie,
} from '@/utils/tenant-cache.utils'

function buildFallbackTenantConfig(tenantId: string, hostname: string): TenantConfig {
  const displayName =
    hostnameToBrandName(hostname) || tenantId.charAt(0).toUpperCase() + tenantId.slice(1)

  return {
    id: tenantId,
    name: displayName,
    subdomain: tenantId,
    theme: {
      primaryColor: '#007bff',
      secondaryColor: '#6c757d',
    },
    features: [],
    status: 'ACTIVE',
  }
}

function hasTenantCustomization(config: TenantConfig | null | undefined): boolean {
  return Boolean(config?.customization && Object.keys(config.customization).length > 0)
}

export interface FetchTenantConfigResult {
  config: TenantConfig | null
  /** Confirmed missing community on a non-FSDS community host (not a transient API failure). */
  communityNotFound: boolean
  tenantId: string | null
}

const EMPTY_RESULT: FetchTenantConfigResult = {
  config: null,
  communityNotFound: false,
  tenantId: null,
}

/**
 * Loads tenant config during SSR so crawlers receive media defaults and branding.
 * On community hosts, a confirmed missing community returns `communityNotFound: true`
 * instead of a synthetic fallback config.
 */
export async function fetchTenantConfigServer(
  req: IncomingMessage,
  res?: ServerResponse
): Promise<FetchTenantConfigResult> {
  const hostname = (req.headers.host || '').split(':')[0]
  let tenantId = getTenantFromRequest({
    headers: req.headers as IncomingMessage['headers'],
    cookies: req.headers.cookie ? parseCookieHeader(req.headers.cookie) : undefined,
  })

  if (!tenantId || tenantId === 'default') {
    tenantId = await resolveTenantSubdomainFromHostname(hostname)
  }

  if (!tenantId || tenantId === 'default') {
    return EMPTY_RESULT
  }

  const cached = getTenantConfigFromCookie({
    cookies: parseCookieHeader(req.headers.cookie || ''),
  })

  const useFsdsPlatformConfig = usesFsdsPlatformTenantConfig(hostname)

  if (cached?.id === tenantId && hasTenantCustomization(cached)) {
    if (!useFsdsPlatformConfig) {
      if (!isCommunityPubliclyAccessible(cached.status)) {
        return { config: null, communityNotFound: true, tenantId }
      }

      try {
        const community = await communityConfigService.getBySubdomain(tenantId)
        if (!community) {
          return { config: null, communityNotFound: true, tenantId }
        }
      } catch {
        // Transient lookup failure — serve cached branding rather than blocking the site.
      }
    }

    let config = cached
    if (!useFsdsPlatformConfig) {
      try {
        const mediaDefaults = await communityMediaDefaultService.getBySubdomain(tenantId)
        config = {
          ...cached,
          theme: communityMediaDefaultService.mergeThemeWithMediaDefaults(cached.theme, mediaDefaults),
          customization: communityMediaDefaultService.mergeIntoCustomization(
            cached.customization as TenantCustomization | undefined,
            mediaDefaults
          ),
        }
      } catch {
        // Use cached config without refreshing media
      }
    }
    baseService.setTenantConfig(tenantId, config)
    return { config, communityNotFound: false, tenantId }
  }

  if (useFsdsPlatformConfig) {
    baseService.setTenantConfig(tenantId, buildFallbackTenantConfig(tenantId, hostname))
  }

  try {
    let config: TenantConfig | null = null

    if (!useFsdsPlatformConfig) {
      config = await communityConfigService.getTenantConfigBySubdomain(tenantId)
      if (!config) {
        return { config: null, communityNotFound: true, tenantId }
      }
    } else {
      const headers: Record<string, string> = {
        'x-tenant-id': tenantId,
      }

      if (hostname) {
        headers[APP_HOST_HEADER] = hostname
        const forwardedProto = req.headers['x-forwarded-proto']
        const protocol =
          typeof forwardedProto === 'string' && forwardedProto === 'http' ? 'http' : 'https'
        headers.Origin = `${protocol}://${hostname}`
      }

      const response = await baseService.getClient().get<TenantConfig>(`/tenants/config/${tenantId}`, {
        headers,
      })
      config = response.data
    }

    if (!config) {
      const fallback = buildFallbackTenantConfig(tenantId, hostname)
      baseService.setTenantConfig(tenantId, fallback)
      return { config: fallback, communityNotFound: false, tenantId }
    }

    if (config.name) {
      config.name = normalizeTenantDisplayName(config.name)
    }

    baseService.setTenantConfig(tenantId, config)

    if (res) {
      setTenantConfigCookie(res, tenantId, config)
    }

    return { config, communityNotFound: false, tenantId }
  } catch {
    // Transient failures: keep fallback on FSDS platform hosts only.
    // On community hosts, do not invent a tenant that was never confirmed.
    if (!useFsdsPlatformConfig) {
      return { config: null, communityNotFound: false, tenantId }
    }

    const fallback = buildFallbackTenantConfig(tenantId, hostname)
    baseService.setTenantConfig(tenantId, fallback)
    return { config: fallback, communityNotFound: false, tenantId }
  }
}

export interface TenantSocialMeta {
  siteName: string
  description: string
  ogImage: string
}

export function buildTenantSocialMeta(
  hostname: string,
  tenantConfig: TenantConfig | null
): TenantSocialMeta {
  const isFsdsDomain = isFsdsHostname(hostname)
  const tenantFaviconUrl =
    tenantConfig?.customization?.mediaDefaults?.defaultFaviconUrl?.trim() || ''
  const branding = resolveRuntimeBrandingMetadata({
    hostname,
    tenantName: tenantConfig?.name,
    tenantLogo: tenantConfig?.theme?.logo as string | undefined,
    tenantFaviconUrl,
  })
  const defaultThumbnailUrl = resolveDefaultThumbnailUrl(tenantConfig?.customization, {
    isFsdsDomain,
  })
  const ogImagePath = resolveSeoOgImageUrl(undefined, {
    isFsdsDomain,
    defaultThumbnailUrl,
  })

  return {
    siteName: branding.siteName,
    description: isFsdsDomain
      ? 'Khám phá các khóa học toàn diện về Full Stack Data Science.'
      : 'Explore comprehensive courses and resources on our data platform.',
    ogImage: getAbsoluteUrl(ogImagePath, hostname),
  }
}

function parseCookieHeader(cookieHeader: string): Record<string, string> {
  return cookieHeader.split(';').reduce<Record<string, string>>((acc, part) => {
    const [key, ...rest] = part.trim().split('=')
    if (key) {
      acc[key] = decodeURIComponent(rest.join('='))
    }
    return acc
  }, {})
}
